import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Divider,
  Form,
} from 'antd';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { getIdMap } from 'ontraccr-common/lib/Common';
import { getContactAddressBooks } from 'ontraccr-common/lib/Contacts';

import CustomModal from '../../../common/modals/CustomModal';
import OnTraccrButton from '../../../common/buttons/OnTraccrButton';
import colors from '../../../constants/Colors';
import FormSectionPermissions from './FormSectionPermissions';
import ConditionalRenderingRow from '../FormFields/ConditionalRenderingRow';
import OnTraccrCheckbox from '../../../common/inputs/OnTraccrCheckbox';

export default function FormSectionSettingsModal({
  id,
  visible,
  onSettingsUpdated,
  onCloseClicked,
  initialValues = {},
  divisions,
  costcodes,
  allowRepeatingSections = true,
  isExternalForm,
  enablePermissions,
  sections = [],
  isTimeCard,
}) {
  const {
    hasConditionalRendering: initialHasConditionalRendering = false,
    conditionalRenderingFormula: initialConditionalRenderingFormula,
    isDuplicationEnabled: initialIsDuplicationEnabled = false,
  } = initialValues || {};

  const projects = useSelector((state) => state.projects.projects);
  const phases = useSelector((state) => state.costcodes.phases);
  const customers = useSelector((state) => state.customers.customers);
  const users = useSelector((state) => state.users.users);
  const vendors = useSelector((state) => state.vendors.vendors);
  const equipment = useSelector((state) => state.equipment.equipment);
  const formTemplates = useSelector((state) => state.forms.templates);
  const labels = useSelector((state) => state.labels);
  const globalAddressBooks = useSelector((state) => state.contacts.globalAddressBooks);
  const buckets = useSelector((state) => state.buckets.buckets);
  const scheduleCategories = useSelector((state) => state.schedule.categories);

  const [
    hasConditionalRendering,
    setHasConditionalRendering,
  ] = useState(initialHasConditionalRendering);
  const [
    conditionalRenderingFormula,
    setConditionalRenderingFormula,
  ] = useState(initialConditionalRenderingFormula);
  const [isDuplicationEnabled, setIsDuplicationEnabled] = useState(initialIsDuplicationEnabled);

  const projectIdMap = useMemo(() => getIdMap(projects), [projects]);
  const contactAddressBooks = useMemo(() => (
    getContactAddressBooks(globalAddressBooks)
  ), [globalAddressBooks]);

  const [form] = Form.useForm();

  const onSubmit = useCallback(() => {
    const values = form.getFieldsValue(true);
    onSettingsUpdated({
      ...values,
      hasConditionalRendering,
      conditionalRenderingFormula,
    });
    onCloseClicked();
  }, [form, onSettingsUpdated, hasConditionalRendering, conditionalRenderingFormula]);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setHasConditionalRendering(false);
      setConditionalRenderingFormula();
      setIsDuplicationEnabled(false);
    }
  }, [visible, form]);

  useEffect(() => {
    if (visible && initialValues) {
      setHasConditionalRendering(initialHasConditionalRendering);
      setConditionalRenderingFormula(initialConditionalRenderingFormula);
      setIsDuplicationEnabled(initialIsDuplicationEnabled);
    }
  }, [visible, initialValues]);

  return (
    <CustomModal
      title="Section Settings"
      visible={visible}
      onCloseClicked={onCloseClicked}
      maskColor={colors.MODAL_MASK_DARK}
      width={1150}
    >
      <Form
        form={form}
        initialValues={initialValues}
        style={{ paddingLeft: 25, paddingRight: 25 }}
      >
        <Form.Item
          name="defaultCollapsed"
        >
          <OnTraccrCheckbox
            label="Default Collapsed"
            hoverNode={(
              <div style={{ width: 300 }}>
                <p>
                  If you check this box, the section will be{' '}
                  collapsed by default when a user opens the form.
                </p>
                <strong>
                  Warning: Setting this section to default collapsed
                  will make all fields in the section optional.
                </strong>
              </div>
            )}
          />
        </Form.Item>
        {allowRepeatingSections && (
          <Form.Item
            name="isDuplicationEnabled"
          >
            <OnTraccrCheckbox
              label="Enable Section Duplication"
              onChange={setIsDuplicationEnabled}
              hoverNode={(
                <div style={{ width: 300 }}>
                  If you check this box,
                  the section can be duplicated by another user when filling out the form.
                  Note that calculation fields are not supported and
                  only standard templates can be used.
                </div>
              )}
            />
          </Form.Item>
        )}
        {allowRepeatingSections && isDuplicationEnabled && (
          <Form.Item name="isRenamingEnabled" preserve={false}>
            <OnTraccrCheckbox
              label="Allow Section Renaming"
              hoverNode={(
                <div style={{ width: 300 }}>
                  If you check this box, users filling out the form can rename
                  this section and any duplicated copies.
                </div>
              )}
            />
          </Form.Item>
        )}
        <Form.Item
          name="isCheckAllYesNoEnabled"
        >
          <OnTraccrCheckbox
            label="Enable &quot;Check All&quot; for Yes/No Fields"
            hoverNode={(
              <div style={{ width: 300 }}>
                If you check this box, a &quot;Check All&quot; option will be available for{' '}
                Yes/No fields in the section. If clicked on, a pop up will appear allowing{' '}
                the user to mark all visible fields as Yes, No, or N/A.
              </div>
            )}
          />
        </Form.Item>
        {!isExternalForm && enablePermissions && (
          <>
            <Divider />
            <Form.Item
              name="permissions"
              trigger="onPermissionChanged"
              valuePropName="permissions"
            >
              <FormSectionPermissions
                divisions={divisions}
              />
            </Form.Item>
          </>
        )}
        {!isTimeCard && (
          <ConditionalRenderingRow
            id={id}
            onChange={setHasConditionalRendering}
            onFormulaChange={setConditionalRenderingFormula}
            hasConditionalRendering={hasConditionalRendering}
            conditionalRenderingFormula={conditionalRenderingFormula}
            sections={sections}
            customers={customers}
            projects={projects}
            users={users}
            costcodes={costcodes}
            phases={phases}
            projectIdMap={projectIdMap}
            vendors={vendors}
            equipment={equipment}
            formTemplates={formTemplates}
            labels={labels}
            contactAddressBooks={contactAddressBooks}
            divisions={divisions}
            buckets={buckets}
            scheduleCategories={scheduleCategories}
            isSection
          />
        )}
      </Form>
      <OnTraccrButton
        title="Save"
        roundStyle
        style={{ margin: 10 }}
        onClick={onSubmit}
      />
    </CustomModal>
  );
}

FormSectionSettingsModal.propTypes = {
  id: PropTypes.string,
  visible: PropTypes.bool.isRequired,
  onSettingsUpdated: PropTypes.func.isRequired,
  onCloseClicked: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    defaultCollapsed: PropTypes.bool,
    isDuplicationEnabled: PropTypes.bool,
    isCheckAllYesNoEnabled: PropTypes.bool,
    isRenamingEnabled: PropTypes.bool,
  }),
  divisions: PropTypes.arrayOf(PropTypes.shape({})),
  costcodes: PropTypes.arrayOf(PropTypes.shape({})),
  allowRepeatingSections: PropTypes.bool,
  isExternalForm: PropTypes.bool,
  sections: PropTypes.arrayOf(PropTypes.shape({})),
  isTimeCard: PropTypes.bool,
};

FormSectionSettingsModal.defaultProps = {
  id: null,
  initialValues: {},
  divisions: undefined,
  costcodes: [],
  allowRepeatingSections: true,
  isExternalForm: false,
  sections: [],
  isTimeCard: false,
};
